
import { useState, useEffect, useCallback } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from '../../auth/AuthContext';

const COMPANY_METADATA_PROGRAM = {
  ID: 'NkReudUAW4F',
  STAGE_ID: 'htIWgNQWEMm',
  DATA_ELEMENTS: {
    EMAIL: 'NkCxPqLeDpq',
    ORG_TYPE: 'QIMf5a1r1Ot',
    FIN_START: 'U55MqwBcRAe',
    FIN_END: 'kgdk4NXu4f5',
    COMPANY_LOGO: 'muysgcGfkyN',
    KRA_VISIBILITY: 'GRC9YcQRXsx'
  }
};

export const useKraVisibility = () => {
  const engine = useDataEngine();
  const { user } = useAuth();
  const [isKraEnabled, setIsKraEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const getOrgUnitId = useCallback(() => {
    return user?.organisationUnits?.[0]?.id || null;
  }, [user]);

  const isAdmin = useCallback(() => {
    return user?.userCredentials?.userRoles?.some(role => 
      role.name?.toLowerCase().includes('admin') || 
      role.id === 'nLSSLch316I'
    ) || false;
  }, [user]);

  const loadKraVisibility = useCallback(async () => {
    const orgId = getOrgUnitId();
    if (!orgId) {
      setIsKraEnabled(false);
      setLoading(false);
      return false;
    }

    try {
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: COMPANY_METADATA_PROGRAM.ID,
            programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
            orgUnit: orgId,
            ouMode: 'ACCESSIBLE',
            pageSize: 1,
            order: 'eventDate:desc',
            fields: 'event,eventDate,dataValues[dataElement,value]'
          }
        }
      });

      const event = response.events?.events?.[0];
      
      if (!event) {
        setIsKraEnabled(false);
        setLoading(false);
        return false;
      }

      const kraValue = event.dataValues?.find(
        dv => dv.dataElement === COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.KRA_VISIBILITY
      )?.value;

      let enabled = false;
      if (kraValue === true || kraValue === 'true' || kraValue === '1' || kraValue === 1) {
        enabled = true;
      }
      
      setIsKraEnabled(enabled);
      setLoading(false);
      return enabled;
    } catch (error) {
      console.error('Failed to load KRA visibility:', error);
      setError(error.message);
      setIsKraEnabled(false);
      setLoading(false);
      return false;
    }
  }, [engine, getOrgUnitId]);

  const saveKraVisibility = useCallback(async (enabled) => {
    if (!isAdmin()) {
      throw new Error('Only administrators can change this setting');
    }

    const orgId = getOrgUnitId();
    if (!orgId) {
      throw new Error('No organisation unit found for user');
    }

    setSaving(true);
    setError(null);

    try {
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: COMPANY_METADATA_PROGRAM.ID,
            programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
            orgUnit: orgId,
            ouMode: 'ACCESSIBLE',
            pageSize: 1,
            order: 'eventDate:desc',
            fields: 'event,eventDate,dataValues[dataElement,value]'
          }
        }
      });

      const existingEvent = response.events?.events?.[0];
      let existingDataValues = existingEvent?.dataValues || [];
      const existingEventId = existingEvent?.event;

      const filteredDataValues = existingDataValues.filter(
        dv => dv.dataElement !== COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.KRA_VISIBILITY
      );

      const updatedDataValues = [
        ...filteredDataValues,
        {
          dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.KRA_VISIBILITY,
          value: String(enabled)
        }
      ];

      const today = new Date().toISOString().split('T')[0];

      if (existingEventId) {
        const fullEventResponse = await engine.query({
          fullEvent: {
            resource: `events/${existingEventId}`,
            params: {
              fields: 'trackedEntityInstance,enrollment'
            }
          }
        });

        const fullEvent = fullEventResponse.fullEvent;

        const eventPayload = {
          program: COMPANY_METADATA_PROGRAM.ID,
          programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
          orgUnit: orgId,
          eventDate: existingEvent.eventDate,
          status: 'COMPLETED',
          trackedEntityInstance: fullEvent?.trackedEntityInstance,
          enrollment: fullEvent?.enrollment,
          event: existingEventId,
          dataValues: updatedDataValues
        };

        const updateResponse = await fetch(`/api/events/${existingEventId}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(eventPayload)
        });

        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          if (updateResponse.status === 409) {
            // Delete and create on conflict
            await fetch(`/api/events/${existingEventId}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include'
            });
            delete eventPayload.event;
            const createResponse = await fetch('/api/events', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify(eventPayload)
            });
            if (!createResponse.ok) {
              throw new Error(`Create failed: ${createResponse.status}`);
            }
          } else {
            throw new Error(`Update failed: ${updateResponse.status} - ${errorText}`);
          }
        }
      } else {
        // Create new event
        let teiId = null;
        let enrollmentId = null;

        try {
          const teiResponse = await engine.query({
            teis: {
              resource: 'trackedEntityInstances',
              params: {
                program: COMPANY_METADATA_PROGRAM.ID,
                ou: orgId,
                ouMode: 'SELECTED',
                fields: 'trackedEntityInstance',
                pageSize: 1
              }
            }
          });
          teiId = teiResponse.teis?.trackedEntityInstances?.[0]?.trackedEntityInstance;
        } catch (error) {
          console.warn('TEI query error:', error);
        }

        if (!teiId) {
          const createTeiResponse = await fetch('/api/trackedEntityInstances', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              trackedEntityType: 'RwXRVqlEw3l',
              orgUnit: orgId,
              attributes: []
            })
          });
          const teiResult = await createTeiResponse.json();
          teiId = teiResult?.response?.importSummaries?.[0]?.reference || 
                  teiResult?.response?.uid || 
                  teiResult?.uid;
        }

        try {
          const enrollmentResponse = await engine.query({
            enrollments: {
              resource: 'enrollments',
              params: {
                trackedEntityInstance: teiId,
                program: COMPANY_METADATA_PROGRAM.ID,
                ou: orgId,
                fields: 'enrollment',
                pageSize: 1
              }
            }
          });
          enrollmentId = enrollmentResponse.enrollments?.enrollments?.[0]?.enrollment;
        } catch (error) {
          console.warn('Enrollment query error:', error);
        }

        if (!enrollmentId) {
          const createEnrollmentResponse = await fetch('/api/enrollments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              trackedEntityInstance: teiId,
              program: COMPANY_METADATA_PROGRAM.ID,
              orgUnit: orgId,
              enrollmentDate: today,
              incidentDate: today,
              status: 'ACTIVE'
            })
          });
          const enrollmentResult = await createEnrollmentResponse.json();
          enrollmentId = enrollmentResult?.response?.importSummaries?.[0]?.reference ||
                         enrollmentResult?.enrollment ||
                         enrollmentResult?.response?.uid;
        }

        const eventPayload = {
          program: COMPANY_METADATA_PROGRAM.ID,
          programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
          orgUnit: orgId,
          eventDate: today,
          status: 'COMPLETED',
          trackedEntityInstance: teiId,
          enrollment: enrollmentId,
          dataValues: updatedDataValues
        };

        const createResponse = await fetch('/api/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(eventPayload)
        });

        if (!createResponse.ok) {
          throw new Error(`Create failed: ${createResponse.status}`);
        }
      }

      // ✅ IMPORTANT: Update state and reset saving
      setIsKraEnabled(enabled);
      setSaving(false);
      setError(null);
      return true;
    } catch (error) {
      console.error('Failed to save KRA visibility:', error);
      setError(error.message);
      setSaving(false);
      throw error;
    }
  }, [engine, isAdmin, getOrgUnitId]);

  useEffect(() => {
    loadKraVisibility();
  }, [loadKraVisibility]);

  return {
    isKraEnabled,
    loading,
    saving,
    error,
    saveKraVisibility,
    loadKraVisibility,
    isAdmin: isAdmin(),
    orgUnitId: getOrgUnitId(),
  };
};